/**
 * SceneTreeSearchBar — debounced text input for filtering the Scene Tree.
 *
 * P3 — INIT-2026-Q3-SCENE-HIERARCHY (viewer tree completion).
 * Debounces input at 300ms before propagating the query to the parent.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { tokens } from "@/design";

export interface SceneTreeSearchBarProps {
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
}

export function SceneTreeSearchBar({
  query,
  onQueryChange,
}: SceneTreeSearchBarProps) {
  const [localValue, setLocalValue] = useState(query);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalValue(query);
  }, [query]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setLocalValue(val);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => onQueryChange(val), 300);
    },
    [onQueryChange],
  );

  const handleClear = useCallback(() => {
    setLocalValue("");
    onQueryChange("");
    if (timerRef.current) clearTimeout(timerRef.current);
  }, [onQueryChange]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div
      data-testid="scene-tree-search-bar"
      style={{
        display: "flex",
        alignItems: "center",
        gap: tokens.space[1],
        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
        borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        backgroundColor: tokens.color.surface[1],
        flexShrink: 0,
      }}
    >
      <span
        aria-hidden
        style={{
          fontFamily: "Material Symbols Rounded",
          fontSize: tokens.font.size.md,
          lineHeight: 1,
          color: tokens.color.ink.muted,
        }}
      >
        search
      </span>
      <input
        type="search"
        aria-label="Search scene tree"
        placeholder="Filter nodes…"
        value={localValue}
        onChange={handleChange}
        style={{
          flex: 1,
          height: tokens.density.rowDense,
          border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
          borderRadius: tokens.radius.pill,
          padding: `0 ${tokens.space[2]}px`,
          fontSize: tokens.font.size.sm,
          fontFamily: tokens.font.family.ui,
          color: tokens.color.ink.primary,
          backgroundColor: tokens.color.surface[0],
          outline: "none",
        }}
      />
      {localValue && (
        <button
          aria-label="Clear search"
          onClick={handleClear}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: tokens.density.rowDense - tokens.space[1],
            height: tokens.density.rowDense - tokens.space[1],
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            color: tokens.color.ink.muted,
          }}
        >
          <span
            aria-hidden
            style={{
              fontFamily: "Material Symbols Rounded",
              fontSize: tokens.font.size.md,
              lineHeight: 1,
            }}
          >
            close
          </span>
        </button>
      )}
    </div>
  );
}
